import { useState } from "react";
import { FinancialSummary } from "./FinancialSummary";
import { TransactionsDetail } from "./TransactionsDetail";
import { CashFlowChart } from "./CashFlowChart";
import { CategoryChart } from "./CategoryChart";
import { FinancialInsights } from "./FinancialInsights";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileDown } from "lucide-react";
import { FinancialFilter } from "./FinancialFilter";
import { exportReport } from "@/utils/exportReport";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export function FinancialReport() {
  const [period, setPeriod] = useState<"month" | "quarter" | "year">("month");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const { data: transactions } = useQuery({
    queryKey: ["transactions", startDate, endDate],
    queryFn: async () => {
      let query = supabase
        .from("transactions")
        .select("*")
        .order("created_at", { ascending: false });

      if (startDate) {
        query = query.gte("created_at", startDate);
      }
      if (endDate) {
        query = query.lte("created_at", endDate);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  const handleExport = async () => {
    try {
      if (!transactions?.length) {
        toast.error("Não há dados para exportar");
        return;
      }

      const formattedData = transactions.map(transaction => ({
        Data: new Date(transaction.created_at).toLocaleDateString(),
        Tipo: transaction.type,
        Categoria: transaction.category,
        Valor: transaction.amount,
        Status: transaction.payment_status,
        "Método de Pagamento": transaction.payment_method,
      }));

      await exportReport(formattedData, `relatorio-financeiro-${new Date().toISOString().split('T')[0]}`);
      toast.success("Relatório exportado com sucesso!");
    } catch (error) {
      console.error("Erro ao exportar relatório:", error);
      toast.error("Erro ao exportar relatório");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold text-[#0073B7] whitespace-normal break-words">
          Relatório Financeiro
        </h2>
        <Button
          onClick={handleExport}
          className="bg-[#0073B7] hover:bg-[#004080] text-white w-full sm:w-auto"
        >
          <FileDown className="mr-2 h-4 w-4" />
          Exportar Relatório
        </Button>
      </div>

      <div className="space-y-6">
        <Card className="p-4 md:p-6 bg-[#1A1F2C] border-[#404040]">
          <FinancialFilter
            period={period}
            setPeriod={setPeriod}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
        </Card>

        <Card className="p-4 md:p-6 bg-[#1A1F2C] border-[#404040]">
          <FinancialSummary period={period} startDate={startDate} endDate={endDate} />
        </Card>

        <Card className="p-4 md:p-6 bg-[#1A1F2C] border-[#404040]">
          <h3 className="text-lg font-semibold text-white mb-6">
            Fluxo de Caixa
          </h3>
          <div className="h-[400px] w-full">
            <CashFlowChart period={period} startDate={startDate} endDate={endDate} />
          </div>
        </Card>

        <Card className="p-4 md:p-6 bg-[#1A1F2C] border-[#404040]">
          <h3 className="text-lg font-semibold text-white mb-6">
            Distribuição por Categoria
          </h3>
          <div className="h-[400px] w-full">
            <CategoryChart startDate={startDate} endDate={endDate} />
          </div>
        </Card>

        <Card className="p-4 md:p-6 bg-[#1A1F2C] border-[#404040]">
          <TransactionsDetail period={period} startDate={startDate} endDate={endDate} />
        </Card>

        <Card className="p-4 md:p-6 bg-[#1A1F2C] border-[#404040]">
          <FinancialInsights period={period} startDate={startDate} endDate={endDate} />
        </Card>
      </div>
    </div>
  );
}